// The picture of a bar model: a whole bar over its parts, or two bars to
// compare. Plain React SVG, like practice-count.tsx. Every bar has its number
// or a bold "?" printed in it, and the parts are told apart by a line between
// them as well as by their shading.

import {
  BAR_LABEL_SIZE,
  BAR_NAME_SIZE,
  barModelPicture,
  describeBarModel,
  type BarBox,
  type BarModelSpec,
} from './practice-bar-model-data';

const INK = '#2d4a3e';
const FILLS: Record<BarBox['role'], string> = {
  whole: '#cfe3d4',
  part: '#f6ecc9',
  longer: '#cfe3d4',
  shorter: '#f6ecc9',
  difference: '#ffffff',
};
// Every other part is a little darker, so two parts never look like one.
const PART_FILL_ALT = '#f0d9a0';

function Bar({ bar, index }: { bar: BarBox; index: number }) {
  const unknown = bar.value === '?';
  const fill =
    bar.role === 'part' && index % 2 === 1 ? PART_FILL_ALT : FILLS[bar.role];
  return (
    <g data-bar={bar.role} data-value={String(bar.value)}>
      <rect
        x={bar.x}
        y={bar.y}
        width={bar.width}
        height={bar.height}
        fill={fill}
        stroke={INK}
        strokeWidth="2"
        // The difference is the gap the shorter bar would need: dashed.
        strokeDasharray={bar.role === 'difference' ? '5 4' : undefined}
      />
      {/* A short tick ties a number printed under its bar to that bar. */}
      {bar.labelBelow ? (
        <line
          x1={bar.x + bar.width / 2}
          x2={bar.x + bar.width / 2}
          y1={bar.y + bar.height}
          y2={bar.y + bar.height + 5}
          stroke={INK}
          strokeWidth="1.5"
        />
      ) : null}
      <text
        x={bar.x + bar.width / 2}
        y={bar.labelY}
        dy="0.35em"
        textAnchor="middle"
        fill={INK}
        fontSize={BAR_LABEL_SIZE}
        fontWeight={unknown ? '700' : '400'}
      >
        {String(bar.value)}
      </text>
    </g>
  );
}

export function BarModelPictureView({ barModel }: { barModel: BarModelSpec }) {
  const picture = barModelPicture(barModel);
  return (
    <figure className="m-0 max-w-md">
      <svg
        role="img"
        aria-label={describeBarModel(barModel)}
        viewBox={`0 0 ${String(picture.width)} ${String(picture.height)}`}
        className="mx-auto block h-auto w-full"
        style={{ maxWidth: `${String(Math.round(picture.width * 1.4))}px` }}
      >
        {/* The whole comes first, so the parts count from 1. */}
        {picture.bars.map((bar, index) => (
          <Bar key={index} bar={bar} index={index - 1} />
        ))}
        {picture.names.map((name) => (
          <text
            key={name.text + String(name.y)}
            data-name="true"
            x={name.x}
            y={name.y}
            dy="0.35em"
            fill={INK}
            fontSize={BAR_NAME_SIZE}
            fontWeight="700"
          >
            {name.text}
          </text>
        ))}
      </svg>
    </figure>
  );
}
